
import { toPng, toJpeg } from 'html-to-image';
import { jsPDF } from 'jspdf';

/**
 * Exports the Gantt chart as an image file (PNG or JPEG)
 */
export const exportGanttToImage = async ({
    ganttRef,
    format = 'png',
    tasks = [],
    boxes = {},
    scale = 1,
    boxWidth = 75,
    boxheight = 54,
    duration = 10,
    duration_with_working_days = 0,
    duration_with_delay = 0,
    updateXarrow
}) => {
    if (!ganttRef.current) return;
    
    try {
        
        const pixelRatio = 3;
        
        // Calculate the dimensions for the full chart
        let maxWidth = 0;
        let maxHeight = 0;
        
        // Use all days from the chart
        const endDay = Math.max(
            duration || 10,
            duration_with_working_days,
            duration_with_delay
        );
        
        
        
        // Calculate width to include all tasks
        // Get project header width
        const projectHeaderWidth = duration_with_delay * boxWidth + boxWidth;
        
        // Find the rightmost task position by checking each task's position and width
        let maxTaskEndPosition = 0;
        tasks.slice(1).forEach(task => {
            const taskStart = (task.start_day + 1) * boxWidth;
            const taskWidth = (task.duration + (task.delayed_duration || 0)) * boxWidth;
            const taskEndPosition = taskStart + taskWidth;
            maxTaskEndPosition = Math.max(maxTaskEndPosition, taskEndPosition);
        });
        
        // Use the maximum of project width and task positions, plus some padding
        maxWidth = Math.max(projectHeaderWidth, maxTaskEndPosition) + (boxWidth * 2);
        
        // Calculate proper height to include all tasks
        
        // First find maximum Y position from positioned tasks
        let maxY = 0;
        Object.values(boxes).forEach(box => {
            maxY = Math.max(maxY, box.y);
        });
        
        // Add height for all tasks plus padding
        const totalTasks = tasks.length;
        const taskSpacing = scale * 20;
        const totalTasksHeight = totalTasks * (boxheight + taskSpacing);
        
        // Calculate top padding for date headers (scales with the scale factor)
        const topHeaderPadding = 60 * scale;
        
        // Use compact spacing values
        const verticalPaddingFactor = 1;
        const additionalPadding = 50;
        const taskCountMultiplier = 1;

        // Ensure we have enough height for all tasks plus headers and sufficient padding
        maxHeight = Math.max(
            maxY + (boxheight * verticalPaddingFactor),
            totalTasksHeight + additionalPadding,
            window.innerHeight * 0.6
        );
        
        
        maxHeight += topHeaderPadding;
        
        // Add minimal additional padding based on task count
        maxHeight += Math.min(30, tasks.length * taskCountMultiplier);
        
        // console.log(`Exporting image with dimensions: ${maxWidth}x${maxHeight}`); 
        
        // Save the original styles and scroll position to restore later
        const originalStyles = {
            width: ganttRef.current.style.width,
            height: ganttRef.current.style.height,
            overflow: ganttRef.current.style.overflow,
            position: ganttRef.current.style.position,
            scrollLeft: ganttRef.current.scrollLeft,
            scrollTop: ganttRef.current.scrollTop
        };
        
        // Apply the new dimensions directly to the original container
        ganttRef.current.style.width = `${maxWidth}px`;
        ganttRef.current.style.height = `${maxHeight}px`;
        ganttRef.current.style.overflow = 'visible';
        
        // Start from the beginning for full chart export
        ganttRef.current.scrollLeft = 0;
        
        // Update XArrows to ensure dependencies are rendered correctly
        updateXarrow();
        
        // Fix for arrow heads - force opacity to 1 on all arrow elements
        document.querySelectorAll('.xarrow, .xarrow path, .xarrow g').forEach(el => {
            if (el.style.opacity === '0') {
                el.style.opacity = '1';
            }
            
            // Also find and trigger any animations to complete them
            const animations = el.querySelectorAll('animate');
            animations.forEach(anim => {
                if (anim.getAttribute('begin') === 'indefinite') {
                    anim.beginElement();
                }
            });
        });
        
        // Wait a moment for the DOM to update and animations to complete
        await new Promise(resolve => setTimeout(resolve, 500)); // Longer wait for more reliable rendering
        
        // Prepare options for export with correct dimensions
        const options = {
            quality: 1.0, // Highest quality
            pixelRatio: pixelRatio,
            width: maxWidth,
            height: maxHeight,
            backgroundColor: document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff',
            filter: (node) => {
                // Keep all nodes, including SVGs (for arrows)
                return true;
            },
            skipFonts: false, // Include fonts for better text rendering
            cacheBust: true, // Avoid caching issues
            canvasWidth: maxWidth,
            canvasHeight: maxHeight,
            style: {
                // Force visibility of all elements
                '.xarrow, .xarrow g, .xarrow path': {
                    opacity: '1 !important'
                }
            }
        };
        
        // Create file name with date and time
        const date = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const projectName = tasks[0]?.name || 'project';
        const fileName = `gantt-chart-${projectName}-high-quality-${date}`;
        
        let dataUrl;
        
        try {
            // For PDF format, we need to first convert to PNG then create a PDF
            if (format === 'pdf') {
                // First generate a PNG image
                dataUrl = await toPng(ganttRef.current, options);
                
                // Create a new PDF document
                const orientation = maxWidth > maxHeight ? 'landscape' : 'portrait';
                const pdf = new jsPDF({
                    orientation: orientation,
                    unit: 'px',
                    format: [maxWidth, maxHeight],
                    compress: true
                });
                
                // Calculate PDF dimensions to fit the image properly
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();
                
                // Calculate scaling factor to fit image within PDF dimensions
                // while maintaining aspect ratio
                const aspectRatio = maxWidth / maxHeight;
                let imgWidth = pdfWidth;
                let imgHeight = pdfWidth / aspectRatio;
                
                // If image height exceeds page height, scale based on height instead
                if (imgHeight > pdfHeight) {
                    imgHeight = pdfHeight;
                    imgWidth = pdfHeight * aspectRatio;
                }
                
                // Add the image to the PDF
                pdf.addImage(
                    dataUrl, 
                    'PNG', 
                    0, 
                    0, 
                    imgWidth, 
                    imgHeight
                );
                
                // Save the PDF
                pdf.save(`${fileName}.pdf`);
            } else {
                // Handle PNG and JPEG as before
                if (format === 'png') {
                    dataUrl = await toPng(ganttRef.current, options);
                } else {
                    dataUrl = await toJpeg(ganttRef.current, options);
                }
                
                // Create download link
                const link = document.createElement('a');
                link.download = `${fileName}.${format}`;
                link.href = dataUrl;
                link.click();
            }
        } finally {
            // Restore the original styles
            ganttRef.current.style.width = originalStyles.width;
            ganttRef.current.style.height = originalStyles.height;
            ganttRef.current.style.overflow = originalStyles.overflow;
            ganttRef.current.style.position = originalStyles.position;
            
            // Restore the original scroll position
            ganttRef.current.scrollLeft = originalStyles.scrollLeft;
            ganttRef.current.scrollTop = originalStyles.scrollTop;
            
            // Update arrows again after restoring
            updateXarrow();
        }
        
        return true;
    } catch (error) {
        // console.error('Error exporting Gantt chart:', error); 
        throw error;
    }
}; 